import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import type { OfferType, PaymentTerm } from '@makemydeal/dr-platform-types';
import { DEFAULT_RATE_TYPE_MAPPING, MAX_MONEY_FACTOR, RATE_TYPE } from '../constants/payment';
import type { NonCashOfferType, RateKey } from '../types/commonTypes';

export const formatAprRate = (amount: number): number => {
    return Number(amount.toFixed(3));
};

export const formatMoneyFactor = (amount: number): number => {
    return Number(amount.toFixed(5));
};

export const convertRatesByType = (rateType: string, buyRate: number | null, sellRate: number | null) => {
    const transformRate = (rate: number) => {
        const isMoneyFactor = rateType === RATE_TYPE.MoneyFactor;
        const conversionFactor = isMoneyFactor ? 1 / 2400 : 2400;
        const transformedRate = rate * conversionFactor;
        return isMoneyFactor ? formatMoneyFactor(transformedRate) : formatAprRate(transformedRate);
    };

    const transformedBuyRate = buyRate != null ? transformRate(buyRate) : undefined;
    const transformedSellRate = sellRate != null ? transformRate(sellRate) : undefined;

    return {
        transformedBuyRate,
        transformedSellRate
    };
};
export const convertRatesFromSourceType = (
    targetRateType: string,
    sourceRateType: string | null | undefined,
    buyRate: number | null,
    sellRate: number | null
) => {
    if (sourceRateType && sourceRateType === targetRateType) {
        return {
            transformedBuyRate: buyRate ?? undefined,
            transformedSellRate: sellRate ?? undefined
        };
    }

    return convertRatesByType(targetRateType, buyRate, sellRate);
};

export const exceedsMoneyFactorBound = (rate?: number | null): boolean => rate != null && rate > MAX_MONEY_FACTOR;

export const isImplausibleMoneyFactor = (rateType: string | null | undefined, ...rates: (number | null | undefined)[]): boolean =>
    rateType === RATE_TYPE.MoneyFactor && rates.some(exceedsMoneyFactorBound);

export const IMPLAUSIBLE_MONEY_FACTOR_ACTION = 'implausibleMoneyFactor';

export const logImplausibleMoneyFactor = (site: string, attributes: Record<string, unknown>): void =>
    logNewRelicPageAction(IMPLAUSIBLE_MONEY_FACTOR_ACTION, { site, ...attributes });

export const refusesImplausibleMoneyFactor = (
    site: string,
    rateType: string | null | undefined,
    rate: number | null | undefined,
    offerType?: string
): boolean => {
    const refused = isImplausibleMoneyFactor(rateType, rate);
    if (refused) logImplausibleMoneyFactor(site, { outcome: 'refused', offerType, rateType, rate });
    return refused;
};

const isMoneyFactorSized = (rate?: number | null): boolean => rate != null && rate > 0 && rate <= MAX_MONEY_FACTOR;

export const isImplausibleApr = (rateType: string | null | undefined, ...rates: (number | null | undefined)[]): boolean =>
    rateType === RATE_TYPE.APR && rates.some(isMoneyFactorSized);

export const isRateInAnotherUnit = (rateType: string | null | undefined, rate?: number | null): boolean =>
    isImplausibleMoneyFactor(rateType, rate) || isImplausibleApr(rateType, rate);

const DEFAULT_RATE_TYPE_BY_OFFER_TYPE: Partial<Record<OfferType, string>> = DEFAULT_RATE_TYPE_MAPPING;

const rateTypeForTerm = (paymentTerm: PaymentTerm, offerType?: OfferType): string | undefined =>
    paymentTerm.rateType ?? (offerType ? DEFAULT_RATE_TYPE_BY_OFFER_TYPE[offerType] : undefined);

export const termHasImplausibleMoneyFactor = (paymentTerm: PaymentTerm, offerType?: OfferType): boolean =>
    isImplausibleMoneyFactor(rateTypeForTerm(paymentTerm, offerType), paymentTerm.sellRate, paymentTerm.buyRate);

export const resolveTermRateType = (paymentTerm: PaymentTerm, offerType: NonCashOfferType): string | undefined =>
    termHasImplausibleMoneyFactor(paymentTerm, offerType) ? undefined : rateTypeForTerm(paymentTerm, offerType);

type RateEntry = Partial<Record<RateKey, number | null>>;

const withoutImplausibleRate = <T extends RateEntry>(entry: T, key: RateKey, rateType: string | null | undefined): T => {
    if (!isImplausibleMoneyFactor(rateType, entry[key])) return entry;

    const { [key]: _staleRate, ...kept } = entry;
    return kept as T;
};

export const withoutImplausibleBuyRate = <T extends RateEntry>(entry: T, rateType: string | null | undefined): T =>
    withoutImplausibleRate(entry, 'buyRate', rateType);

const withoutOrphanedRateType = <T extends RateEntry & { rateType?: unknown }>(cleaned: T, original: T): T => {
    if (original.sellRate == null || cleaned.sellRate != null) return cleaned;

    const { rateType: _orphanedRateType, ...kept } = cleaned;
    return kept as T;
};

export const withoutImplausibleRates = <T extends RateEntry & { rateType?: unknown }>(
    entry: T,
    rateType: string | null | undefined
): T =>
    withoutOrphanedRateType(
        withoutImplausibleRate(withoutImplausibleRate(entry, 'buyRate', rateType), 'sellRate', rateType),
        entry
    );

export const mapLeaseQuoteType = (type?: string | null): 'APR' | 'MoneyFactor' => (type === 'APR' ? 'APR' : 'MoneyFactor');
